<template>
    <div>
        <div v-if="isLoading" class="alert alert-info mt-0">
            <p>Loading...</p>
        </div>
        <div v-else>
            <div v-if="isSubmitted" class="alert alert-success mt-0 mb-4">
                <p class="mb-0">Thank you, we have updated your details.</p>
            </div>

            <div v-if="hasErrors" class="alert alert-danger mt-0 mb-4">
                <ul class="mb-0">
                    <li v-for="(messages, field) in errors" :key="field">
                        <span v-for="message in messages" :key="message">{{ message }}</span>
                    </li>
                </ul>
            </div>
            <form @submit.prevent="submitForm">
                <div class="form-group">
                    <label for="country_id" class="form-label text-md-right">Country</label>
                    <select v-model="form.country_id" id="country_id" class="form-control" :class="{ 'is-invalid': errors.country_id }" required>
                        <option></option>
                        <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
                    </select>
                    <span v-if="errors.country_id" class="invalid-feedback" role="alert">
                        <strong>{{ errors.country_id[0] }}</strong>
                    </span>
                </div>

                <div class="form-group" v-if="form.country_id === 1">
                    <label for="region_id" class="form-label text-md-right">Region</label>
                    <select v-model="form.region_id" id="region_id" class="form-control" :class="{ 'is-invalid': errors.region_id }" required>
                        <option></option>
                        <option v-for="region in regions" :key="region.id" :value="region.id">{{ region.name }}</option>
                    </select>
                    <span v-if="errors.region_id" class="invalid-feedback" role="alert">
                        <strong>{{ errors.region_id[0] }}</strong>
                    </span>
                </div>

                <div class="form-group">
                    <label for="legal_name" class="form-label text-md-right">Legal name</label>
                    <input v-model="form.legal_name" id="legal_name" type="text" class="form-control" :class="{ 'is-invalid': errors.legal_name }" required>
                    <span v-if="errors.legal_name" class="invalid-feedback" role="alert">
                        <strong>{{ errors.legal_name[0] }}</strong>
                    </span>
                    <small class="form-text text-muted">
                        Please enter your full legal name as it appears on your passport or driving licence.
                    </small>
                </div>

                <div class="form-group">
                    <label for="address_1" class="form-label text-md-right">Address 1</label>
                    <input v-model="form.address_1" id="address_1" type="text" class="form-control" :class="{ 'is-invalid': errors.address_1 }" required>
                    <span v-if="errors.address_1" class="invalid-feedback" role="alert">
                        <strong>{{ errors.address_1[0] }}</strong>
                    </span>
                    <small class="form-text text-muted">
                        Please enter your address line 1.
                    </small>
                </div>

                <div class="form-group">
                    <label for="address_2" class="form-label text-md-right">Address 2</label>
                    <input v-model="form.address_2" id="address_2" type="text" class="form-control" :class="{ 'is-invalid': errors.address_2 }">
                    <span v-if="errors.address_2" class="invalid-feedback" role="alert">
                        <strong>{{ errors.address_2[0] }}</strong>
                    </span>
                    <small class="form-text text-muted">
                        Optional
                    </small>
                </div>

                <div class="form-group">
                    <label for="city" class="form-label text-md-right">City</label>
                    <input v-model="form.city" id="city" type="text" class="form-control" :class="{ 'is-invalid': errors.city }" required>
                    <span v-if="errors.city" class="invalid-feedback" role="alert">
                        <strong>{{ errors.city[0] }}</strong>
                    </span>
                </div>

                <div class="form-group">
                    <label for="postal_code" class="form-label text-md-right">Zip or postal code</label>
                    <input v-model="form.postal_code" id="postal_code" type="text" class="form-control" :class="{ 'is-invalid': errors.postal_code }" required>
                    <span v-if="errors.postal_code" class="invalid-feedback" role="alert">
                        <strong>{{ errors.postal_code[0] }}</strong>
                    </span>
                    <small class="form-text text-muted">
                        Please enter your postal code.
                    </small>
                </div>

                <div class="form-group">
                    <label for="phone" class="form-label text-md-right">Phone number</label>
                    <input v-model="form.phone" id="phone" type="text" class="form-control" :class="{ 'is-invalid': errors.phone }" required>
                    <span v-if="errors.phone" class="invalid-feedback" role="alert">
                        <strong>{{ errors.phone[0] }}</strong>
                    </span>
                    <small class="form-text text-muted">
                        Please include your country code. For example, for UK, enter 447712345678.
                    </small>
                </div>

                <div class="form-group justify-content-center row mt-4 mb-0">
                    <div class="col text-center">
                        <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                            {{ submitButtonText }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            countries: [],
            regions: [],
            form: {
                country_id: null,
                region_id: null,
                legal_name: '',
                address_1: '',
                address_2: '',
                city: '',
                postal_code: '',
                phone: ''
            },
            errors: {},
            hasErrors: false,
            isSubmitting: false,
            isLoading: true,
            submitButtonText: 'Update',
            isSubmitted: false,
            isAuth: false,
            user: null,
        }
    },
    mounted() {
        if(window.virtuoso) {
            if(window.virtuoso.is_auth) {
                this.isAuth = true;
                this.user = window.virtuoso.user;
            }
        }

        this.fetchCountries();
    },
    methods: {
        fetchCountries() {
            this.isLoading = true;
            axios.get(`/api/countries`)
                .then(response => {
                    this.countries = response.data.countries;
                    this.regions = response.data.regions;
                    this.fetchUser();
                })
                .catch(error => {
                    console.error('Error fetching countries:', error);
                });
        },

        fetchUser() {
            this.isLoading = true;
            axios.get(`/api/me/get-user`)
                .then(response => {
                    this.user = response.data;
                    this.form = { ...this.form, ...this.user };
                    this.isLoading = false;
                })
                .catch(error => {
                    console.error('Error fetching user:', error);
                });
        },

        validateForm() {
            this.errors = {};

            if (!this.form.country_id) {
                this.errors.country_id = ['Country is required'];
            }

            if (this.form.country_id === 1 && !this.form.region_id) {
                this.errors.region_id = ['Region is required when country is 1'];
            }

            if (!this.form.legal_name) {
                this.errors.legal_name = ['Legal name is required'];
            }

            if (!this.form.address_1) {
                this.errors.address_1 = ['Address 1 is required'];
            }

            if (!this.form.city) {
                this.errors.city = ['City is required'];
            }

            if (!this.form.postal_code) {
                this.errors.postal_code = ['Postal code is required'];
            }

            if (!this.form.phone) {
                this.errors.phone = ['Phone number is required'];
            }

            this.hasErrors = Object.keys(this.errors).length > 0;

            return !this.hasErrors;
        },

        submitForm() {
            if (!this.validateForm()) {
                return;
            }

            this.isSubmitting = true;
            this.hasErrors = false;

            const formData = new FormData();
            for (const key in this.form) {
                if (this.form[key] !== null && this.form[key] !== '') {
                    formData.append(key, this.form[key]);
                }
            }

            axios.post(`/api/me/address/process`, formData)
                .then(response => {
                    this.isSubmitting = false;
                    this.message = response.data.message;
                    this.isSubmitted = true;

                    if(window.location.search.includes('return')) {
                        window.location.href = window.location.search.split('return=')[1];
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data.errors) {
                        this.errors = error.response.data.errors;
                        this.hasErrors = true;
                    }
                    this.isSubmitting = false;
                });
        }
    }
}
</script>
